import {
  Button,
  Checkbox,
  type CheckboxProps,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import { useState } from 'react';

type InfoPopupProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const COOKIE_NAME = 'infoPopupDontShowAgain';
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

// localStorage is isolated to the full host (e.g. 16.us-2.isolation.mimecast.com),
// so a dismissal on one instance host wouldn't apply to others. A cookie scoped to
// the shared parent domain (e.g. isolation.mimecast.com) is sent to every host
// under it, making the preference apply across the whole subdomain.
const getCookieDomain = (): string | undefined => {
  const marker = 'isolation.';
  const index = window.location.hostname.indexOf(marker);
  return index === -1 ? undefined : window.location.hostname.slice(index);
};

const readDontShowAgain = (): boolean =>
  document.cookie.split('; ').some((entry) => entry === `${COOKIE_NAME}=true`);

const writeDontShowAgain = (value: boolean): void => {
  const domain = getCookieDomain();
  document.cookie = [
    `${COOKIE_NAME}=${value ? 'true' : 'false'}`,
    'path=/',
    `max-age=${value ? ONE_YEAR_IN_SECONDS : 0}`,
    'SameSite=Lax',
    domain && `domain=${domain}`,
  ]
    .filter(Boolean)
    .join('; ');
};

const InfoPopup = ({ open, onOpenChange }: InfoPopupProps) => {
  const dontShowAgainStored = readDontShowAgain();
  const [dontShowAgain, setDontShowAgain] =
    useState<CheckboxProps['checked']>(dontShowAgainStored);

  if (dontShowAgainStored && open) {
    onOpenChange(false);
    return null;
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        writeDontShowAgain(dontShowAgain === true);
        onOpenChange(data.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Welcome to Browser Isolation</DialogTitle>
          <DialogContent>
            To protect you on the web, some pages will be displayed in a secure
            browser.
          </DialogContent>
          <DialogActions>
            <Checkbox
              label="Don't show again"
              checked={dontShowAgain}
              onChange={(_, data) => {
                setDontShowAgain(data.checked);
              }}
            />
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary">Continue</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default InfoPopup;
